/**
 * Enum com os tipos de grupos de usuários do sistema
 */
export enum UserGroup {
  ADVISORY_MASTER = 'advisory.master',
  ADVISORY_ADVISOR = 'advisory.advisor',
  BROKER_MASTER = 'broker.master',
  BROKER_BROKER = 'broker.broker',
  INSURER_ADMINISTRATOR = 'insurer.administrator',
  INSURER_CUSTOMER_SUPPORT = 'insurer.customer_support',
  INSURER_UNDERWRITER = 'insurer.underwriter',
  INSURER_COMMERCIAL_MANAGER = 'insurer.commercial_manager',
  INSURER_SALES_EXECUTIVE = 'insurer.sales_executive',
  INSURER_COMMERCIAL_ASSISTANT = 'insurer.commercial_assistant',
  INSURER_FINANCE = 'insurer.finance',
}

/** Perfis comerciais da seguradora — mesma visão full nesta fase. */
export const INSURER_COMMERCIAL_GROUPS: readonly UserGroup[] = [
  UserGroup.INSURER_COMMERCIAL_MANAGER,
  UserGroup.INSURER_SALES_EXECUTIVE,
  UserGroup.INSURER_COMMERCIAL_ASSISTANT,
];

export function isCommercialInsurerUser(userGroups: string[]): boolean {
  return userGroups.some(group =>
    INSURER_COMMERCIAL_GROUPS.includes(group as UserGroup)
  );
}

/**
 * Perfis por escopo, pelo prefixo do grupo — mesmo critério do back
 * (`api/src/shared/utils/get-group.ts`) e do `requireFinancialAccess`. Por
 * prefixo, e não por lista, para que um grupo novo de seguradora não precise
 * ser cadastrado em dois lugares para ser reconhecido.
 */
export function isInsurerUser(userGroups: string[]): boolean {
  return userGroups.some(group => group.startsWith('insurer.'));
}

export function isBrokerUser(userGroups: string[]): boolean {
  return userGroups.some(group => group.startsWith('broker.'));
}

/**
 * Tipos de usuário disponíveis
 */
export type UserType = 'insurer' | 'advisory' | 'broker';

/**
 * Roles por tipo de usuário
 */
export type InsurerRole =
  | 'administrator'
  | 'customer_support'
  | 'underwriter'
  | 'commercial_manager'
  | 'sales_executive'
  | 'commercial_assistant'
  | 'finance';
export type AdvisoryRole = 'advisor' | 'master';
export type BrokerRole = 'broker' | 'master';

/** Union de todas as roles (para uso em getUserGroup) */
export type UserRole = InsurerRole | AdvisoryRole | BrokerRole;

/**
 * Mapeamento userType + role -> grupo Cognito
 */
const GROUP_MAP: Record<UserType, Record<string, string>> = {
  insurer: {
    administrator: 'insurer.administrator',
    customer_support: 'insurer.customer_support',
    underwriter: 'insurer.underwriter',
    commercial_manager: 'insurer.commercial_manager',
    sales_executive: 'insurer.sales_executive',
    commercial_assistant: 'insurer.commercial_assistant',
    finance: 'insurer.finance',
  },
  advisory: {
    advisor: 'advisory.advisor',
    master: 'advisory.master',
  },
  broker: {
    broker: 'broker.broker',
    master: 'broker.master',
  },
};

/**
 * Combina userType e role para gerar o grupo de usuário
 * @param userType - Tipo do usuário (insurer, advisory, broker)
 * @param role - Role do usuário (específica por tipo)
 * @returns String do grupo de usuário correspondente
 */
export function getUserGroup(userType: UserType, role: UserRole): string {
  return GROUP_MAP[userType]?.[role] ?? `${userType}.${role}`;
}

/**
 * Extrai userType e role a partir do groupName (ex: "advisory.master" -> { userType: 'advisory', role: 'master' })
 */
export function getUserTypeAndRoleFromGroup(
  groupName: string
): { userType: UserType; role: UserRole } | null {
  if (!groupName?.includes('.')) return null;
  const [userType, ...roleParts] = groupName.split('.');
  const role = roleParts.join('.') as UserRole;
  if (userType in GROUP_MAP && role in GROUP_MAP[userType as UserType]) {
    return { userType: userType as UserType, role };
  }
  return null;
}

/**
 * Retorna o escopo (Scopo) a partir do groupName
 * insurer → Seguradora, broker → Corretora, advisory → Assessoria
 */
export function getScopeFromGroupName(groupName: string): string {
  if (!groupName) return '-';
  const lower = groupName.toLowerCase();
  if (lower.includes('insurer')) return 'Seguradora';
  if (lower.includes('broker')) return 'Corretora';
  if (lower.includes('advisory')) return 'Assessoria';
  return '-';
}

/** Opções de role para uso em selects, filtradas por tipo de usuário */
function sortRoleOptionsByLabel<T extends { label: string }>(
  options: T[]
): T[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export function getRoleOptionsByUserType(userType: UserType) {
  if (userType === 'insurer') {
    return sortRoleOptionsByLabel([
      { label: 'Administrador', value: 'administrator' },
      { label: 'Assistente Comercial', value: 'commercial_assistant' },
      { label: 'CX Atendimento', value: 'customer_support' },
      { label: 'Executivo de Vendas', value: 'sales_executive' },
      { label: 'Gerente Comercial', value: 'commercial_manager' },
      { label: 'Financeiro', value: 'finance' },
      { label: 'Subscritor', value: 'underwriter' },
    ]);
  }
  if (userType === 'advisory') {
    return sortRoleOptionsByLabel([
      { label: 'Assessor', value: 'advisor' },
      { label: 'Master', value: 'master' },
    ]);
  }
  return sortRoleOptionsByLabel([
    { label: 'Corretor', value: 'broker' },
    { label: 'Master', value: 'master' },
  ]);
}
